import {
    buildAuctionCatalogueHref,
    getCatalogueClosingDate,
    getCataloguePublicCopy,
    isStaggeredAuctionCatalogue,
    normaliseAuctionCataloguePath
} from "../utils/auction-catalogues";
import ChevronRightIcon from "@heroicons/react/solid/ChevronRightIcon";

const formatDate = value => value
    ? new Intl.DateTimeFormat(undefined, {dateStyle: "medium", timeStyle: "short"}).format(new Date(value))
    : "To be confirmed";

const formatScheduleDate = (value, locales = undefined) => {
    if (!value) return "To be confirmed";

    const date = new Date(value);
    const options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    };

    return new Intl.DateTimeFormat(locales, options).format(date);
};

export const formatScheduleDateRange = (startValue, endValue, locales = undefined) => {
    if (!startValue || !endValue) return "To be confirmed";

    const startDate = new Date(startValue);
    const endDate = new Date(endValue);
    if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
        return "To be confirmed";
    }

    const options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    };

    const formatter = new Intl.DateTimeFormat(locales, options);
    return typeof formatter.formatRange === "function"
        ? formatter.formatRange(startDate, endDate)
        : `${formatter.format(startDate)}–${formatter.format(endDate)}`;
};

const getCatalogueCardLabel = catalogue => {
    const prefix = getCataloguePublicCopy(catalogue).eventNameTitle === "Tender"
        ? "Tender Catalogue"
        : "Catalogue";
    return `${prefix} ${catalogue.catalogueNumber}`;
};

const getCatalogueLotLabel = catalogue =>
    `${catalogue.lotCount} ${catalogue.lotCount === 1 ? "Lot" : "Lots"}`;

function CatalogueMetadata({catalogue, className = ""}) {
    return (
        <p className={`mb-0 text-sm font-medium text-gray-500 ${className}`.trim()}>
            {getCatalogueLotLabel(catalogue)}
            <span aria-hidden="true"> · </span>
            {catalogue.catalogueNumber}
        </p>
    );
}

const getCatalogueState = catalogue => String(catalogue?.state || "").trim().toLowerCase();
const isLiveCatalogue = catalogue => getCatalogueState(catalogue) === "live";
const isUpcomingCatalogue = catalogue => ["scheduled", "upcoming"].includes(getCatalogueState(catalogue));

export const getCatalogueLandingSortDate = catalogue => isStaggeredAuctionCatalogue(catalogue)
    ? catalogue?.firstLotEndDate || catalogue?.endDate
    : getCatalogueClosingDate(catalogue);

export const getCatalogueLandingSchedule = (catalogue, status, locales = undefined) => {
    const copy = getCataloguePublicCopy(catalogue);

    if (status !== "live") {
        const value = formatScheduleDate(catalogue?.startDate, locales);
        return {
            label: copy.scheduleStartsLabel,
            value,
            summary: `${copy.scheduleStartsLabel} ${value}`
        };
    }

    if (!isStaggeredAuctionCatalogue(catalogue)) {
        const closingDate = getCatalogueClosingDate(catalogue);
        const value = formatScheduleDate(closingDate, locales);
        return {
            label: copy.scheduleClosesLabel,
            value,
            summary: `${copy.scheduleClosesLabel} ${value}`
        };
    }

    const firstScheduledClose = catalogue?.firstLotEndDate || catalogue?.endDate;
    const finalScheduledClose = catalogue?.scheduledFinalLotEndDate || firstScheduledClose;
    const effectiveFinalClose = catalogue?.effectiveFinalLotEndDate || finalScheduledClose;
    const effectiveFinalCloseTime = new Date(effectiveFinalClose).getTime();
    const finalScheduledCloseTime = new Date(finalScheduledClose).getTime();
    const biddingExtended = Number.isFinite(effectiveFinalCloseTime)
        && Number.isFinite(finalScheduledCloseTime)
        && effectiveFinalCloseTime > finalScheduledCloseTime;

    if (biddingExtended) {
        const value = formatScheduleDate(effectiveFinalClose, locales);
        return {
            label: "Bidding Extended Until",
            value,
            summary: `Bidding Extended Until ${value}`
        };
    }

    const value = formatScheduleDateRange(firstScheduledClose, finalScheduledClose, locales);
    return {
        label: "Scheduled Lot Closings",
        value,
        summary: `Scheduled Lot Closings ${value}`
    };
};

function LiveIndicator() {
    return (
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            <span
                className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 motion-safe:animate-ping"
            />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"/>
        </span>
    );
}

export const sortCataloguesByDate = (catalogues, dateSelector) => [...catalogues].sort((left, right) => {
    const getDate = typeof dateSelector === "function"
        ? dateSelector
        : catalogue => catalogue?.[dateSelector];
    const asTimestamp = catalogue => {
        const value = getDate(catalogue);
        if (!value) return null;

        const timestamp = new Date(value).getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
    };
    const leftDate = asTimestamp(left);
    const rightDate = asTimestamp(right);
    if (leftDate === null && rightDate === null) return 0;
    if (leftDate === null) return 1;
    if (rightDate === null) return -1;
    return leftDate - rightDate;
});

function CatalogueCard({catalogue, baseUri}) {
    return (
        <article className="flex flex-col rounded-theme border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex-1">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {getCatalogueCardLabel(catalogue)}
                </div>
                <h3 className="!mb-3 !text-xl">{catalogue.title}</h3>
                <p className="mb-1 text-sm font-medium">
                    {catalogue.lotCount} {catalogue.lotCount === 1 ? "Lot" : "Lots"}
                </p>
                <p className="mb-0 text-sm text-gray-600">
                    Closed {formatDate(getCatalogueClosingDate(catalogue))}
                </p>
            </div>
            <div className="mt-5">
                <a className="button" href={buildAuctionCatalogueHref(baseUri, catalogue.catalogueNumber)}>
                    View Catalogue
                </a>
            </div>
        </article>
    );
}

function CatalogueList({
    catalogues,
    baseUri,
    emptyMessage = "There are no catalogues to show yet."
}) {
    if (!catalogues.length) return <p className="mb-0 text-base text-gray-600">{emptyMessage}</p>;
    return (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {catalogues.map(catalogue => (
                <CatalogueCard
                    key={catalogue.catalogueNumber}
                    catalogue={catalogue}
                    baseUri={baseUri}
                />
            ))}
        </div>
    );
}

function CatalogueSchedule({catalogue, status}) {
    const schedule = getCatalogueLandingSchedule(catalogue, status);

    return (
        <div className="mt-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {schedule.label}
            </p>
            <p className="mb-0 text-sm font-medium leading-snug text-gray-800">
                {schedule.value}
            </p>
        </div>
    );
}

function LandingCatalogueCard({catalogue, baseUri, status, featured = false}) {
    const live = status === "live";

    return (
        <article className={`flex flex-col rounded-theme border p-5 sm:p-6 ${
            featured
                ? "border-[color:var(--base-colour)] bg-[color:var(--light-background)]"
                : "border-gray-200 bg-white"
        }`}>
            <div className="flex-1">
                {live && (
                    <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                        <LiveIndicator/>
                        <span>Live</span>
                    </div>
                )}
                <h3 className="!mb-1.5 !text-xl !leading-tight">{catalogue.title}</h3>
                <CatalogueMetadata catalogue={catalogue}/>
                <CatalogueSchedule catalogue={catalogue} status={status}/>
            </div>
            <div className="mt-6">
                <a
                    className={live
                        ? "button"
                        : "inline-flex items-center gap-0.5 text-sm font-medium text-primary-600 hover:text-primary-700"}
                    href={buildAuctionCatalogueHref(baseUri, catalogue.catalogueNumber)}
                >
                    {live ? "View Catalogue" : "Preview Catalogue"}
                    {!live && <ChevronRightIcon className="relative top-px h-4 w-4" aria-hidden="true"/>}
                </a>
            </div>
        </article>
    );
}

function LiveCatalogues({catalogues, baseUri, showUpcomingLink = false}) {
    if (!catalogues.length) {
        return (
            <section aria-labelledby="auction-catalogue-live">
                <h2 id="auction-catalogue-live" className="!mb-2 !text-2xl">Open For Bidding</h2>
                <p className="mb-0 text-base text-gray-600">No auctions are open for bidding right now.</p>
                {showUpcomingLink && (
                    <a
                        className="mt-2 inline-flex items-center gap-0.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                        href={`${baseUri}/upcoming`}
                    >
                        View Upcoming Auctions
                        <ChevronRightIcon className="relative top-px h-4 w-4" aria-hidden="true"/>
                    </a>
                )}
            </section>
        );
    }

    const sortedCatalogues = sortCataloguesByDate(catalogues, getCatalogueLandingSortDate);

    return (
        <section aria-labelledby="auction-catalogue-live">
            <h2 id="auction-catalogue-live" className="!mb-5 !text-2xl">Open For Bidding</h2>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-[minmax(0,1.45fr)_repeat(2,minmax(0,1fr))]">
                {sortedCatalogues.map((catalogue, index) => (
                    <LandingCatalogueCard
                        key={catalogue.catalogueNumber}
                        catalogue={catalogue}
                        baseUri={baseUri}
                        status="live"
                        featured={index === 0}
                    />
                ))}
            </div>
        </section>
    );
}

function UpcomingCatalogues({catalogues, baseUri}) {
    const sortedCatalogues = sortCataloguesByDate(catalogues, "startDate");

    return (
        <section aria-labelledby="auction-catalogue-upcoming">
            <h2 id="auction-catalogue-upcoming" className="!mb-5 !text-2xl">Coming Up</h2>
            {sortedCatalogues.length ? (
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-[minmax(0,1.45fr)_repeat(2,minmax(0,1fr))]">
                    {sortedCatalogues.map(catalogue => (
                        <LandingCatalogueCard
                            key={catalogue.catalogueNumber}
                            catalogue={catalogue}
                            baseUri={baseUri}
                            status="upcoming"
                        />
                    ))}
                </div>
            ) : <p className="mb-0 text-base text-gray-600">There are no upcoming auctions scheduled yet.</p>}
        </section>
    );
}

export default function AuctionCatalogueBlock({values = {}}) {
    const baseUri = normaliseAuctionCataloguePath(values.baseUri);

    if (values.mode === "not-found") {
        return (
            <section className="mx-auto w-full max-w-7xl section">
                <p className="text-gray-600">This catalogue may have ended or is no longer available.</p>
                <a className="button" href={baseUri}>View Current Catalogues</a>
            </section>
        );
    }

    if (values.mode === "empty") {
        return (
            <section className="mx-auto w-full max-w-7xl section">
                <div className="rounded-theme border border-gray-200 bg-gray-50 p-6">
                    <h2 className="!mb-2 !text-xl">No Auctions Are Open Right Now</h2>
                    <p className="mb-0 text-gray-600">Check back soon for the next auction.</p>
                </div>
            </section>
        );
    }

    if (values.mode === "live") {
        return (
            <section className="mx-auto w-full max-w-7xl section">
                <LiveCatalogues
                    catalogues={values.catalogues || []}
                    baseUri={baseUri}
                    showUpcomingLink
                />
            </section>
        );
    }

    if (values.mode === "upcoming") {
        return (
            <section className="mx-auto w-full max-w-7xl section">
                <UpcomingCatalogues
                    catalogues={values.catalogues || []}
                    baseUri={baseUri}
                />
            </section>
        );
    }

    if (values.mode === "index") {
        const catalogues = values.catalogues || [];
        const liveCatalogues = catalogues.filter(isLiveCatalogue);
        const upcomingCatalogues = catalogues.filter(isUpcomingCatalogue);

        return (
            <section className="mx-auto w-full max-w-7xl section">
                <div className="space-y-12">
                    <LiveCatalogues
                        catalogues={liveCatalogues}
                        baseUri={baseUri}
                    />
                    <UpcomingCatalogues
                        catalogues={upcomingCatalogues}
                        baseUri={baseUri}
                    />
                </div>
            </section>
        );
    }

    return (
        <section className="mx-auto w-full max-w-7xl section">
            <CatalogueList
                catalogues={values.catalogues || []}
                baseUri={baseUri}
                emptyMessage={values.mode === "archive"
                    ? "There are no past catalogues to show yet."
                    : "There are no current catalogues to show yet."}
            />
        </section>
    );
}
